/**
 * Versteckte Events-Seite: nur per Link erreichbar, nicht im Menü, nicht in der
 * Sitemap, noindex. Zeigt die aktive Veranstaltung und das Anmeldeformular.
 */
import { useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useServerFn } from "@tanstack/react-start";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  blogMarkdownComponents,
  remarkVideoEmbeds,
} from "@/lib/blog/markdown-components";
import { normalizeMarkdown } from "@/lib/markdown-normalize";
import { shareImageMeta } from "@/lib/share-image-meta";
import { CalendarDays, MapPin, Users } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { TurnstileWidget, type TurnstileHandle } from "@/components/security/TurnstileWidget";
import { getPublicEvent, registerEventParticipant } from "@/lib/events.functions";
import {
  eventRegistrationSchema,
  firstEventImageUrl,
  formatEventDate,
  type RegistrationOutcome,
} from "@/lib/events";


export const Route = createFileRoute("/events/")({
  loader: async () => ({ event: await getPublicEvent() }),
  head: ({ loaderData }) => {
    const title = loaderData?.event ? `${loaderData.event.title} | creategy` : "Events | creategy";
    const description = "Anmeldung zu einer Veranstaltung bei creategy.";
    const image = firstEventImageUrl(loaderData?.event?.contentMd);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex, nofollow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        // Ticket LM11: gemeinsame Stelle für das Teilen-Bild (auch für Freebies).
        ...shareImageMeta(image),
      ],
    };
  },

  component: EventsPage,
});

const formSchema = eventRegistrationSchema.omit({ eventId: true, turnstileToken: true });
type FormValues = z.infer<typeof formSchema>;

const fieldBase =
  "peer w-full border-0 border-b border-foreground/30 bg-transparent px-0 pt-6 pb-2 text-base text-foreground placeholder-transparent focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm";
const labelBase =
  "pointer-events-none absolute left-0 top-1 text-sm text-muted-foreground transition-all peer-placeholder-shown:top-6 peer-placeholder-shown:text-base peer-focus:top-1 peer-focus:text-sm";

function EventsPage() {
  const { event } = Route.useLoaderData();

  if (!event) {
    return (
      <Layout>
        <section className="section-y mx-auto w-full max-w-[800px] px-6 md:px-12">
          <h1 className="font-display text-3xl font-extrabold text-primary">Derzeit keine Veranstaltung</h1>
          <p className="mt-4 font-body text-foreground">
            Aktuell ist keine Anmeldung möglich. Schauen Sie gern später wieder vorbei oder{" "}
            <Link to="/kontakt" className="underline underline-offset-4">
              schreiben Sie uns
            </Link>
            .
          </p>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="section-y mx-auto w-full max-w-[1000px] px-6 md:px-12">
        <p className="font-display text-sm font-extrabold uppercase tracking-wide text-brand-wahlen">
          Einladung
        </p>
        <h1 className="mt-3 font-display text-4xl font-extrabold text-primary md:text-5xl">{event.title}</h1>
        <dl className="mt-6 flex flex-wrap gap-x-8 gap-y-3 font-body text-foreground">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-primary" aria-hidden />
            <dt className="sr-only">Wann</dt>
            <dd>{formatEventDate(event.startsAt)}</dd>
          </div>
          {event.location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" aria-hidden />
              <dt className="sr-only">Wo</dt>
              <dd>{event.location}</dd>
            </div>
          )}
          {event.freeSeats !== null && event.registrationOpen && (
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" aria-hidden />
              <dt className="sr-only">Freie Plätze</dt>
              <dd>{event.freeSeats === 0 ? "Ausgebucht" : `${event.freeSeats} freie Plätze`}</dd>
            </div>
          )}
        </dl>

        {event.contentMd && (
          <div className="markdown-body markdown-article markdown-article--wide mt-10">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkVideoEmbeds]}
              components={blogMarkdownComponents}
            >
              {normalizeMarkdown(event.contentMd)}
            </ReactMarkdown>
          </div>
        )}
      </section>

      <section className="mx-auto w-full max-w-[800px] px-6 pb-24 md:px-12">
        <h2 className="font-display text-2xl font-extrabold text-primary">Anmeldung</h2>
        {!event.registrationOpen || event.freeSeats === 0 ? (
          <p className="mt-4 rounded-md border border-border bg-muted/30 p-4 font-body text-foreground">
            {event.freeSeats === 0 && event.registrationOpen
              ? "Die Veranstaltung ist ausgebucht."
              : event.closedMessage}
          </p>
        ) : (
          <RegistrationForm eventId={event.id} title={event.title} freeSeats={event.freeSeats} />
        )}
      </section>
    </Layout>
  );
}

function RegistrationForm({
  eventId,
  title,
  freeSeats,
}: {
  eventId: string;
  title: string;
  freeSeats: number | null;
}) {
  const submit = useServerFn(registerEventParticipant);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<RegistrationOutcome | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      plusOne: false,
      privacy: false as unknown as true,
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (!turnstileToken) {
      toast.error("Bitte Bot-Schutz-Prüfung abwarten.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submit({ data: { ...values, eventId, turnstileToken } });
      setOutcome(result);
      if (result.status !== "ok") turnstileRef.current?.reset();
    } catch {
      setSubmitError("Ihre Anmeldung wurde nicht übernommen. Ihre Eingaben sind erhalten – bitte erneut absenden.");
      turnstileRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  if (outcome?.status === "ok") {
    return (
      <div className="mt-6 rounded-md border border-brand-wahlen/40 bg-brand-wahlen/10 p-6 font-body text-foreground">
        <p className="font-display text-lg font-extrabold text-primary">Vielen Dank – Sie sind angemeldet.</p>
        <p className="mt-2">
          Eine Bestätigung zu „{title}“ ist auf dem Weg in Ihr Postfach. Darin finden Sie auch einen Link,
          falls Sie doch absagen müssen.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-10" noValidate>
      {outcome?.status === "full" && (
        <p className="rounded-md border border-destructive/30 bg-destructive/10 p-3 font-body text-sm text-destructive" role="alert">
          Leider reichen die freien Plätze nicht mehr aus
          {outcome.free === 1 ? " – es ist nur noch ein Platz frei (ohne Begleitung)." : "."}
        </p>
      )}
      {outcome?.status === "duplicate" && (
        <p className="rounded-md border border-border bg-muted/30 p-3 font-body text-sm" role="alert">
          Mit dieser E-Mail-Adresse liegt bereits eine Anmeldung vor.
        </p>
      )}
      {outcome?.status === "closed" && (
        <p className="rounded-md border border-border bg-muted/30 p-3 font-body text-sm" role="alert">
          Die Anmeldung ist inzwischen geschlossen.
        </p>
      )}

      <div className="grid gap-8 md:grid-cols-2">
        <div className="relative">
          <input id="ev-firstname" type="text" placeholder="Vorname" autoComplete="given-name" className={fieldBase} {...register("firstName")} />
          <label htmlFor="ev-firstname" className={labelBase}>Vorname</label>
          {errors.firstName && <p className="mt-2 text-xs text-destructive">{errors.firstName.message}</p>}
        </div>
        <div className="relative">
          <input id="ev-lastname" type="text" placeholder="Nachname" autoComplete="family-name" className={fieldBase} aria-invalid={!!errors.lastName} {...register("lastName")} />
          <label htmlFor="ev-lastname" className={labelBase}>Nachname *</label>
          {errors.lastName && <p className="mt-2 text-xs text-destructive">{errors.lastName.message}</p>}
        </div>
      </div>
      <div className="grid gap-8 md:grid-cols-2">
        <div className="relative">
          <input id="ev-email" type="email" placeholder="E-Mail" autoComplete="email" className={fieldBase} aria-invalid={!!errors.email} {...register("email")} />
          <label htmlFor="ev-email" className={labelBase}>E-Mail-Adresse *</label>
          {errors.email && <p className="mt-2 text-xs text-destructive">{errors.email.message}</p>}
        </div>
        <div className="relative">
          <input id="ev-phone" type="tel" placeholder="Telefon" autoComplete="tel" className={fieldBase} aria-invalid={!!errors.phone} {...register("phone")} />
          <label htmlFor="ev-phone" className={labelBase}>Telefonnummer *</label>
          {errors.phone && <p className="mt-2 text-xs text-destructive">{errors.phone.message}</p>}
        </div>
      </div>

      <label className="flex items-start gap-3 font-body text-sm text-foreground">
        <input type="checkbox" className="mt-1 h-4 w-4" disabled={freeSeats === 1} {...register("plusOne")} />
        <span>
          Ich komme mit Begleitung (+1)
          {freeSeats === 1 && <span className="block text-xs text-muted-foreground">Es ist nur noch ein Platz frei.</span>}
        </span>
      </label>

      <label className="flex items-start gap-3 font-body text-sm text-foreground">
        <input type="checkbox" className="mt-1 h-4 w-4" aria-invalid={!!errors.privacy} {...register("privacy")} />
        <span>
          Ich habe die{" "}
          <Link to="/datenschutz" className="underline underline-offset-4">
            Datenschutzerklärung
          </Link>{" "}
          gelesen und bin einverstanden, dass meine Angaben zur Organisation der Veranstaltung verarbeitet
          werden. *
          {errors.privacy && <span className="block text-xs text-destructive">{errors.privacy.message}</span>}
        </span>
      </label>

      <div className="space-y-2">
        <TurnstileWidget action="event"
          ref={turnstileRef}
          context="event"
          onToken={(token) => {
            setTurnstileToken(token);
            if (token) setTurnstileError(null);
          }}
          onError={(message) => setTurnstileError(message)}
        />
        {turnstileError && <p className="font-body text-sm text-destructive" role="alert">{turnstileError}</p>}
      </div>

      {submitError && <p className="font-body text-sm text-destructive" role="alert">{submitError}</p>}

      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center justify-center rounded-md bg-brand-button px-6 py-3 font-display text-sm font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
      >
        {submitting ? "Wird gesendet…" : "Verbindlich anmelden"}
      </button>
    </form>
  );
}
